import React from 'react';

import { ResponsiveChartContainer } from '@mui/x-charts/ResponsiveChartContainer';
import { ChartsGrid } from '@mui/x-charts/ChartsGrid';
import {
    AreaPlot,
    LinePlot,
    LineHighlightPlot,
    areaElementClasses,
} from '@mui/x-charts/LineChart';
import { ChartsYAxis } from '@mui/x-charts/ChartsYAxis';
import { ChartsXAxis } from '@mui/x-charts/ChartsXAxis';
import { ChartsAxisHighlight } from '@mui/x-charts';

import { AxisId } from 'ff/debrief/models';
import CustomTooltip from 'ff/debrief/components/charts/tooltip/CustomTooltip';
import Scrubber from 'ff/debrief/components/charts/Scrubber';
import {
    createSeriesConfigure,
    getTimeSeriesAxisConfig,
    getYAxisConfig,
} from 'ff/debrief/components/charts/utils';

import { BlueLinearGradient } from 'ff/debrief/components/charts/defs';

interface Props {
    timestamps?: Date[];
    currentTime?: Date;
    leftAxisId?: AxisId;
    rightAxisId?: AxisId;
    leftAxisData?: number[];
    rightAxisData?: number[];
    tooltipBounds?: React.RefObject<HTMLDivElement>;
}

const GRADIENT_ID = 'altitude-gradient';

const FlightChart = ({
    timestamps,
    currentTime,
    leftAxisId,
    rightAxisId,
    leftAxisData,
    rightAxisData,
    tooltipBounds,
    children,
}: React.PropsWithChildren<Props>) => {
    return (
        <ResponsiveChartContainer
            xAxis={[getTimeSeriesAxisConfig(timestamps)]}
            yAxis={[
                getYAxisConfig(leftAxisId)(leftAxisData),
                getYAxisConfig(rightAxisId)(rightAxisData),
            ]}
            series={[
                createSeriesConfigure(leftAxisId)(leftAxisData),
                createSeriesConfigure(rightAxisId)(rightAxisData),
            ]}
            sx={{
                userSelect: 'none',
                [`& .${areaElementClasses.root}`]: {
                    fill: `url(#${GRADIENT_ID})`,
                },
            }}
            margin={{ left: 8, right: 8 }}
            height={300}
        >
            <defs>
                <BlueLinearGradient id={GRADIENT_ID} />
            </defs>
            <ChartsGrid
                sx={{ '& .MuiChartsGrid-line': { strokeDasharray: '4 6' } }}
                horizontal
            />
            <ChartsYAxis
                position="left"
                axisId={leftAxisId}
                tickLabelStyle={{
                    textAnchor: 'start',
                    paddingBottom: 10,
                }}
                disableLine
                disableTicks
            />
            <ChartsYAxis
                position="right"
                axisId={rightAxisId}
                tickLabelStyle={{
                    textAnchor: 'end',
                    top: 5,
                }}
                disableLine
                disableTicks
            />
            <ChartsXAxis
                position="bottom"
                tickLabelStyle={{ transform: 'translateY(8px)' }}
                disableLine
                disableTicks
            />
            <AreaPlot />
            <LinePlot />
            <ChartsAxisHighlight x={'line'} />
            <LineHighlightPlot />
            <CustomTooltip boundaryEl={tooltipBounds} />
            <Scrubber currentTime={currentTime} />
            {children}
        </ResponsiveChartContainer>
    );
};

export default FlightChart;
